import HomePage from "./Pages/Home Pages/homePage";
import WarehousePage from "./Pages/Warehouse Pages/warehousePage";
import WarehouseCreationPage from "./Pages/Warehouse Pages/warehouseCreationPage";
import WarehouseEditPage from "./Pages/Warehouse Pages/warehouseEditPage";

import {
  BrowserRouter as Router,
  HashRouter,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { App as CapacitorApp } from "@capacitor/app";

import ItemPage from "./Pages/Item Pages/itemPage";
import ItemCreationPage from "./Pages/Item Pages/itemCreationPage";
import ItemEditPage from "./Pages/Item Pages/itemEditPage";
import LowStockPage from "./Pages/Item Pages/lowStockPage";
import TransferStockPage from "./Pages/Item Pages/transferStockPage";
import ImportItemsPage from "./Pages/Item Pages/importItemsPage";
import StockTakePage from "./Pages/Item Pages/stockTakePage";

import TagPage from "./Pages/Tag Pages/tagPage";

import TrafficPage from "./Pages/Traffic Pages/trafficPage";

import ClientPage from "./Pages/Client Pages/clientPage";
import ClientCreationPage from "./Pages/Client Pages/clientCreationPage";
import ClientEditPage from "./Pages/Client Pages/clientEditPage";

import UnitPage from "./Pages/Unit Pages/unitPage";

import NoteCreationPage from "./Pages/Traffic Pages/noteCreationPage";
import NoteViewPage from "./Pages/Traffic Pages/noteViewPage";

import GeneralSettingsPage from "./Pages/Settings Pages/generalSettingsPage";
import CompanySettingsPage from "./Pages/Settings Pages/companySettingsPage";

import LoginPage from "./Pages/User Pages/loginPage";
import RegisterPage from "./Pages/User Pages/registerPage";
import UserSettingsPage from "./Pages/User Pages/userSettingsPage";
import VerificationPage from "./Pages/Verification Pages/VerificationPage";
import VerifiedPage from "./Pages/Verification Pages/VerifiedPage";
import NotVerifiedPage from "./Pages/Verification Pages/NotVerifiedPage";
import PasswordResetSentPage from "./Pages/Verification Pages/PasswordResetSentPage";
import PasswordChangePage from "./Pages/Verification Pages/PasswordChangePage";

import RolePage from "./Pages/Role Pages/rolePage";
import RoleCreationPage from "./Pages/Role Pages/roleCreationPage";
import RoleEditPage from "./Pages/Role Pages/roleEditPage";

import MessagePage from "./Pages/Message Pages/messagePage";
import MessageViewPage from "./Pages/Message Pages/messageViewPage";
import AnnouncementPage from "./Pages/Message Pages/announcementPage";

import EmployeePage from "./Pages/Employee Pages/employeePage";
import EmployeeViewPage from "./Pages/Employee Pages/employeeEditPage";

import NotFoundPage from "./Pages/notFoundPage";
import AccessDeniedPage from "./Pages/accessDeniedPage";

import ActivityPage from "./Pages/Data View Pages/ActivityPage";
import ReportPage from "./Pages/Data View Pages/ReportPage";

import PackagePage from "Pages/User Pages/packagePage";

import { NotificationProvider } from "./Contexts/NotificationContext";

import { PopupProvider } from "./Contexts/PopupContext";

import { TutorialProvider } from "./Contexts/TutorialContext";

import { LoginProvider, useLogin } from "./Contexts/LoginContext";

import { MessageProvider } from "./Contexts/MessageContext";

import { SettingsProvider } from "./Contexts/SettingsContext";
import { useSettings } from "./Contexts/SettingsContext";

import { AnimatePresence } from "framer-motion";

import { GoogleOAuthProvider } from "@react-oauth/google";

import { GetAccessiblePages } from "./BackendAccess/UserHooks";

import SideBar from "./Components/Other Components/sideBar";
import NavBar from "./Components/Other Components/navBar";

import React, { useEffect, useState, useRef, useCallback } from "react";

import "./i18n";

const ProtectedRoute = ({ element, permission, accessiblePages }) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!permission || accessiblePages.includes(permission)) {
      return;
    }

    navigate("/workspace/accessDeniedPage", { replace: true });
  }, [permission, accessiblePages, navigate]);

  return !permission || accessiblePages.includes(permission) ? element : null;
};

export function usePullToRefresh() {
  const pullToRefreshRef = useRef(null);
  const lastVibrationTime = useRef(0);
  const startYRef = useRef(0);
  const isPullingRef = useRef(false);
  const isRefreshingRef = useRef(false);
  const inFixedPosRef = useRef(false);

  // Constants outside render to avoid recreations
  const REFRESH_HEIGHT = 100;
  const SNAP_THRESHOLD = 30;
  const VIBRATION_COOLDOWN = 100;
  const RESISTANCE = 0.6;

  const tryVibrate = useCallback((duration = 1) => {
    const now = Date.now();
    if (
      navigator.vibrate &&
      now - lastVibrationTime.current > VIBRATION_COOLDOWN
    ) {
      lastVibrationTime.current = now;
      try {
        navigator.vibrate(duration);
      } catch (err) {}
    }
  }, []);

  const resetPullToRefresh = () => {
    if (!pullToRefreshRef.current) return;
    requestAnimationFrame(() => {
      pullToRefreshRef.current.style.transition =
        "height 0.3s ease-out, opacity 0.3s ease-out";
      pullToRefreshRef.current.style.height = "0px";
      pullToRefreshRef.current.style.opacity = "0";
    });
  };

  const handleTouchStart = useCallback((e) => {
    if (window.scrollY <= 5) {
      startYRef.current = e.touches[0].clientY;
      isPullingRef.current = true;

      if (pullToRefreshRef.current) {
        pullToRefreshRef.current.style.transition = "";
      }
    }
  }, []);

  const handleTouchMove = useCallback(
    (e) => {
      if (
        !isPullingRef.current ||
        isRefreshingRef.current ||
        !pullToRefreshRef.current
      )
        return;

      const currentY = e.touches[0].clientY;
      const diffY = currentY - startYRef.current;

      if (diffY > 0) {
        const height = Math.min(diffY * RESISTANCE, 100);

        requestAnimationFrame(() => {
          if (!pullToRefreshRef.current) return;

          if (
            diffY * RESISTANCE > REFRESH_HEIGHT - SNAP_THRESHOLD &&
            diffY * RESISTANCE < REFRESH_HEIGHT + SNAP_THRESHOLD &&
            !inFixedPosRef.current
          ) {
            inFixedPosRef.current = true;
            tryVibrate();
            pullToRefreshRef.current.style.height = `${REFRESH_HEIGHT}px`;
          } else if (
            inFixedPosRef.current &&
            diffY * RESISTANCE < REFRESH_HEIGHT - SNAP_THRESHOLD
          ) {
            inFixedPosRef.current = false;
            pullToRefreshRef.current.style.height = `${height}px`;
          } else if (!inFixedPosRef.current) {
            pullToRefreshRef.current.style.height = `${height}px`;
          }

          pullToRefreshRef.current.style.opacity = `${Math.min(
            (height + SNAP_THRESHOLD) / REFRESH_HEIGHT,
            1
          )}`;
        });

        if (diffY > 10) {
          e.preventDefault();
        }
      }
    },
    [tryVibrate]
  );

  const handleTouchEnd = useCallback(() => {
    if (
      !isPullingRef.current ||
      isRefreshingRef.current ||
      !pullToRefreshRef.current
    )
      return;

    isPullingRef.current = false;
    const height = parseInt(pullToRefreshRef.current.style.height) || 0;

    if (height >= REFRESH_HEIGHT) {
      isRefreshingRef.current = true;
      pullToRefreshRef.current.classList.add("refreshing");
      pullToRefreshRef.current.style.height = `${REFRESH_HEIGHT}px`;
      pullToRefreshRef.current.style.opacity = "1";

      setTimeout(() => {
        tryVibrate();
        window.location.reload();
      }, 500);
    } else {
      resetPullToRefresh();
    }
  }, [resetPullToRefresh, tryVibrate]);

  useEffect(() => {
    document.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    });

    document.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });

    document.addEventListener("touchend", handleTouchEnd);

    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  return { pullToRefreshRef };
}
function AnimatedRoutes() {
  const navigate = useNavigate();
  const isAndroid = /Android/i.test(navigator.userAgent);
  const location = useLocation();

  const [accessiblePages, setAccessiblePages] = useState([]);
  const [loading, setLoading] = useState(true);

  const {
    userSettings,
    companySettings,
    refreshUserSettings,
    refreshCompanySettings,
  } = useSettings();
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);

  const { loggedIn } = useLogin();

  useEffect(() => {
    logInSequence();
  }, [loggedIn]);
  const fetchAccessiblePages = async () => {
    const [data, error] = await GetAccessiblePages();
    console.log("Accessible pages data:", data, error);
    if (Array.isArray(data)) {
      // The [false] sentinel (not yet registered/no package) stays as-is.
      setAccessiblePages(data);
    } else if (typeof data === "string" && data !== "") {
      setAccessiblePages(data.split(","));
    } else {
      setAccessiblePages([]);
    }
    if (
      (data?.length === 0 || (typeof data === "string" && data === "")) &&
      checkIfShouldRedirect()
    ) {
      navigate("/workspace/messages");
    } else {
    }
    setLoading(false);
  };

  const fetchSettings = async () => {
    await refreshUserSettings();
    await refreshCompanySettings();
  };

  const checkIfShouldRedirect = () => {
    if (
      location.pathname === "/" ||
      location.pathname.includes("/home") ||
      location.pathname.includes("/login") ||
      location.pathname.includes("/accessDeniedPage")
    ) {
      return true;
    } else {
      return false;
    }
  };

  const logInSequence = async () => {
    try {
      await fetchAccessiblePages();
      await fetchSettings();
    } finally {
      if (loggedIn) {
        setNotificationsEnabled(userSettings.notificationsEnabled);
      }
    }
  };

  useEffect(() => {
    if (loggedIn && accessiblePages?.length > 0) {
      if (checkIfShouldRedirect()) {
        if (accessiblePages[0] === false) {
          navigate("/settings/packagePage");
        } else {
          navigate("/workspace/" + (userSettings?.startPage ?? "warehouses"));
        }
      }
      setNotificationsEnabled(userSettings.notificationsEnabled);
    }
  }, [loggedIn && accessiblePages]);

  const pathname = location.pathname;

  useEffect(() => {
    const handleBackButton = (e) => {
      e.preventDefault();
      const currentPath = location.pathname;
      const exitRoutes = ["/", "/home", "/login"];

      if (exitRoutes.includes(currentPath)) {
        CapacitorApp.exitApp();
      } else {
        navigate(-1);
      }
    };

    document.addEventListener("backbutton", handleBackButton, false);

    return () => {
      document.removeEventListener("backbutton", handleBackButton, false);
    };
  }, [navigate, location.pathname]);

  let navbarType = "home";
  if (pathname.startsWith("/settings")) {
    navbarType = "settings";
  } else if (pathname.startsWith("/workspace")) {
    navbarType = "workspace";
  } else if (pathname.startsWith("/dataView")) {
    navbarType = "dataView";
  }

  // Permission mapping for routes
  const routePermissions = {
    "/workspace/warehouses": "WarehouseView",
    "/workspace/warehouses/warehouseCreation": "WarehouseCreate",
    "/workspace/warehouses/warehouseEditPage": "WarehouseEdit",
    "/workspace/items": "ItemView",
    "/workspace/items/lowStock": "ItemView",
    "/workspace/items/transferStock": "NoteCreate",
    "/workspace/items/importItems": "ItemCreate",
    "/workspace/items/stockTake": "NoteCreate",
    "/workspace/items/itemCreationPage": "ItemCreate",
    "/workspace/items/itemEditPage": "ItemEdit",
    "/workspace/tags": "TagView",
    "/workspace/traffic": "NoteView",
    "/workspace/traffic/noteCreationPage": "NoteCreate",
    "/workspace/traffic/noteViewPage": "NoteView",
    "/workspace/clients": "ClientView",
    "/workspace/clients/clientCreationPage": "ClientCreate",
    "/workspace/clients/clientInfoPage": "ClientEdit",
    "/workspace/units": "UnitView",
    "/workspace/employees": "EmployeeView",
    "/workspace/employees/employeeViewPage": "EmployeeView",
    "/workspace/roles": "RoleView",
    "/workspace/roles/roleCreationPage": "RoleCreate",
    "/workspace/roles/roleEditPage": "RoleEdit",
    "/settings/company": "ViewCompanyInfo",
    "/dataView/activity": "ActivityView",
    "/dataView/report": "ActivityView",
  };
  const { pullToRefreshRef } = usePullToRefresh();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loadingBackground">
          <div className="spinner"></div>
          <div className="loading-text">
            <h2>Loading</h2>
            <div className="dots">
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div
      className="page-wrapper"
      data-theme={userSettings.theme}
      data-scale={userSettings.scale}
    >
      <div className="page-background"></div>
      <div className="page-routes-container">
        <MessageProvider>
          <NotificationProvider
            notificationsEnabled={userSettings?.notificationsEnabled ?? true}
          >
            <NavBar navType={navbarType} />
            <div
              id="pull-to-refresh"
              ref={pullToRefreshRef}
              className="pull-to-refresh"
            >
              <div className="pull-to-refresh-spinner"></div>
            </div>
            <SideBar />
            <TutorialProvider>
            <PopupProvider>
              <AnimatePresence mode="wait">
                <Routes location={location} key={location.pathname}>
                  {!isAndroid ? (
                    <Route index element={<HomePage />} />
                  ) : (
                    <Route index element={<LoginPage />} />
                  )}
                  {!isAndroid && <Route path="/home" element={<HomePage />} />}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  {/* Verification pages */}
                  <Route
                    path="/verificationPage"
                    element={<VerificationPage />}
                  />
                  <Route path="/verifiedPage" element={<VerifiedPage />} />
                  <Route
                    path="/notVerifiedPage"
                    element={<NotVerifiedPage />}
                  />
                  <Route
                    path="/passwordResetSentPage"
                    element={<PasswordResetSentPage />}
                  />
                  <Route
                    path="/passwordChangePage"
                    element={<PasswordChangePage />}
                  />
                  {/* Workspace links */}
                  <Route
                    path="/workspace/warehouses"
                    element={
                      <ProtectedRoute
                        element={<WarehousePage />}
                        permission={routePermissions["/workspace/warehouses"]}
                        accessiblePages={accessiblePages}
                      />
                    }
                  />
                  <Route
                    path="/workspace/warehouses/warehouseCreation"
                    element={
                      <ProtectedRoute
                        element={<WarehouseCreationPage />}
                        permission={
                          routePermissions[
                            "/workspace/warehouses/warehouseCreation"
                          ]
                        }
                        accessiblePages={accessiblePages}
                      />
                    }
                  />
                  <Route
                    path="/workspace/warehouses/warehouseEditPage"
                    element={
                      <ProtectedRoute
                        element={<WarehouseEditPage />}
                        permission={
                          routePermissions[
                            "/workspace/warehouses/warehouseEditPage"
                          ]
                        }
                        accessiblePages={accessiblePages}
                      />
                    }
                  />
                  <Route
                    path="/workspace/items"
                    element={
                      <ProtectedRoute
                        element={<ItemPage />}
                        permission={routePermissions["/workspace/items"]}
                        accessiblePages={accessiblePages}
                      />
                    }
                  />
                  <Route
                    path="/workspace/items/lowStock"
                    element={
                      <ProtectedRoute
                        element={<LowStockPage />}
                        permission={routePermissions["/workspace/items/lowStock"]}
                        accessiblePages={accessiblePages}
                      />
                    }
                  />
                  <Route
                    path="/workspace/items/transferStock"
                    element={
                      <ProtectedRoute
                        element={<TransferStockPage />}
                        permission={
                          routePermissions["/workspace/items/transferStock"]
                        }
                        accessiblePages={accessiblePages}
                      />
                    }
                  />
                  <Route
                    path="/workspace/items/importItems"
                    element={
                      <ProtectedRoute
                        element={<ImportItemsPage />}
                        permission={
                          routePermissions["/workspace/items/importItems"]
                        }
                        accessiblePages={accessiblePages}
                      />
                    }
                  />
                  <Route
                    path="/workspace/items/stockTake"
                    element={
                      <ProtectedRoute
                        element={<StockTakePage />}
                        permission={
                          routePermissions["/workspace/items/stockTake"]
                        }
                        accessiblePages={accessiblePages}
                      />
                    }
                  />
                  <Route
                    path="/workspace/items/itemCreationPage"
                    element={
                      <ProtectedRoute
                        element={<ItemCreationPage />}
                        permission={
                          routePermissions["/workspace/items/itemCreationPage"]
                        }
                        accessiblePages={accessiblePages}
                      />
                    }
                  />
                  <Route
                    path="/workspace/items/itemEditPage"
                    element={
                      <ProtectedRoute
                        element={<ItemEditPage />}
                        permission={
                          routePermissions["/workspace/items/itemEditPage"]
                        }
                        accessiblePages={accessiblePages}
                      />
                    }
                  />
                  <Route
                    path="/workspace/tags"
                    element={
                      <ProtectedRoute
                        element={<TagPage />}
                        permission={routePermissions["/workspace/tags"]}
                        accessiblePages={accessiblePages}
                      />
                    }
                  />
                  <Route
                    path="/workspace/traffic"
                    element={
                      <ProtectedRoute
                        element={<TrafficPage />}
                        permission={routePermissions["/workspace/traffic"]}
                        accessiblePages={accessiblePages}
                      />
                    }
                  />
                  <Route
                    path="/workspace/clients"
                    element={
                      <ProtectedRoute
                        element={<ClientPage />}
                        permission={routePermissions["/workspace/clients"]}
                        accessiblePages={accessiblePages}
                      />
                    }
                  />
                  <Route
                    path="/workspace/clients/clientCreationPage"
                    element={
                      <ProtectedRoute
                        element={<ClientCreationPage />}
                        permission={
                          routePermissions[
                            "/workspace/clients/clientCreationPage"
                          ]
                        }
                        accessiblePages={accessiblePages}
                      />
                    }
                  />
                  <Route
                    path="/workspace/clients/clientInfoPage"
                    element={
                      <ProtectedRoute
                        element={<ClientEditPage />}
                        permission={
                          routePermissions["/workspace/clients/clientInfoPage"]
                        }
                        accessiblePages={accessiblePages}
                      />
                    }
                  />
                  <Route
                    path="/workspace/units"
                    element={
                      <ProtectedRoute
                        element={<UnitPage />}
                        permission={routePermissions["/workspace/units"]}
                        accessiblePages={accessiblePages}
                      />
                    }
                  />
                  <Route
                    path="/workspace/traffic/noteCreationPage"
                    element={
                      <ProtectedRoute
                        element={<NoteCreationPage />}
                        permission={
                          routePermissions[
                            "/workspace/traffic/noteCreationPage"
                          ]
                        }
                        accessiblePages={accessiblePages}
                      />
                    }
                  />
                  <Route
                    path="/workspace/traffic/noteViewPage"
                    element={
                      <ProtectedRoute
                        element={<NoteViewPage />}
                        permission={
                          routePermissions["/workspace/traffic/noteViewPage"]
                        }
                        accessiblePages={accessiblePages}
                      />
                    }
                  />
                  <Route
                    path="/workspace/roles"
                    element={
                      <ProtectedRoute
                        element={<RolePage />}
                        permission={routePermissions["/workspace/roles"]}
                        accessiblePages={accessiblePages}
                      />
                    }
                  />
                  <Route
                    path="/workspace/roles/roleCreationPage"
                    element={
                      <ProtectedRoute
                        element={<RoleCreationPage />}
                        permission={
                          routePermissions["/workspace/roles/roleCreationPage"]
                        }
                        accessiblePages={accessiblePages}
                      />
                    }
                  />
                  <Route
                    path="/workspace/roles/roleEditPage"
                    element={
                      <ProtectedRoute
                        element={<RoleEditPage />}
                        permission={
                          routePermissions["/workspace/roles/roleEditPage"]
                        }
                        accessiblePages={accessiblePages}
                      />
                    }
                  />
                  <Route path="/workspace/messages" element={<MessagePage />} />
                  <Route
                    path="/workspace/messages/messageViewPage"
                    element={<MessageViewPage />}
                  />
                  <Route
                    path="/workspace/messages/announcementPage"
                    element={
                      <ProtectedRoute
                        element={<AnnouncementPage />}
                        permission="SendAnnouncement"
                        accessiblePages={accessiblePages}
                      />
                    }
                  />
                  <Route
                    path="/workspace/employees"
                    element={
                      <ProtectedRoute
                        element={<EmployeePage />}
                        permission={routePermissions["/workspace/employees"]}
                        accessiblePages={accessiblePages}
                      />
                    }
                  />
                  <Route
                    path="/workspace/employees/employeeViewPage"
                    element={
                      <ProtectedRoute
                        element={<EmployeeViewPage />}
                        permission={
                          routePermissions[
                            "/workspace/employees/employeeViewPage"
                          ]
                        }
                        accessiblePages={accessiblePages}
                      />
                    }
                  />
                  <Route
                    path="/settings/general"
                    element={<GeneralSettingsPage />}
                  />
                  <Route path="/settings/user" element={<UserSettingsPage />} />
                  <Route
                    path="/settings/company"
                    element={
                      <ProtectedRoute
                        element={<CompanySettingsPage />}
                        permission={routePermissions["/settings/company"]}
                        accessiblePages={accessiblePages}
                      />
                    }
                  />
                  <Route
                    path="/settings/packagePage"
                    element={<PackagePage />}
                  />

                  <Route
                    path="/dataView/activity"
                    element={
                      <ProtectedRoute
                        element={<ActivityPage />}
                        permission={routePermissions["/dataView/activity"]}
                        accessiblePages={accessiblePages}
                      />
                    }
                  />
                  <Route
                    path="/dataView/report"
                    element={
                      <ProtectedRoute
                        element={<ReportPage />}
                        permission={routePermissions["/dataView/report"]}
                        accessiblePages={accessiblePages}
                      />
                    }
                  />
                  <Route
                    path="/workspace/accessDeniedPage"
                    element={<AccessDeniedPage />}
                  />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </AnimatePresence>
            </PopupProvider>
            </TutorialProvider>
          </NotificationProvider>
        </MessageProvider>
      </div>
    </div>
  );
}

function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <HashRouter>
        <SettingsProvider>
          <LoginProvider>
            <AnimatedRoutes />
          </LoginProvider>
        </SettingsProvider>
      </HashRouter>
    </GoogleOAuthProvider>
  );
}

export default App;
